import { createEvent, createStore } from 'effector'
import { useStore } from 'effector-react'
import { useCallback, useEffect, useState } from 'react'

import { logger } from '../../../lib/logger'
import { useAVNConfig } from '../../../stages/AVN'
import { CarData, ProductTypeProps } from '../../car/types'
import { setCreditAvailable, setCreditFilterEnabled, setWidgetId } from '../../credit-filter-form/model'
import { $tradeInEnabled, setTradeInEnabled, setTradeInPrice, widgetIDByDealerChanged } from '../model'
import { WidgetIds } from '../types'
import { SmartContractListInitEvent, SmartContractListMethods, SmartContractListState } from './types'

export type ListSmartContractAPI = {
  state: SmartContractListState | null
  api: SmartContractListMethods
  loading: boolean
  failed: boolean
  init: (cars: CarData[], isNew: boolean) => void
  tradeInAvailable: boolean
  creditAvailable: boolean
  tradeInEnabled: boolean
}

let methods: SmartContractListMethods = {
  showTradeIn: () => {},
  enableTradeIn: () => {},
  disableTradeIn: () => {},
  updateCarList: () => {},
  updateCarPrices: () => {},
  openWidget: (config: any) => {}
}

const $state = createStore<null | SmartContractListState>(null)
const setState = createEvent<SmartContractListState>()
$state.on(setState, (_, state) => state)
let initRequested = false

export const useListSmartContractAPI = (): ListSmartContractAPI => {
  const { carViewSettings } = useAVNConfig()
  const state = useStore($state)
  const [failed, setFailed] = useState(false)

  const init = useCallback(async (cars: CarData[], isNew: boolean) => {
    if (!initRequested) {
      initRequested = true

      document.addEventListener('kodix:smartContract:bootedList', ((e: CustomEvent<SmartContractListInitEvent>) => {
        logger.log('[SC/kodix:smartContract:bootedList]', e.detail)
        const data = e.detail.api.v1.data
        methods = e.detail.api.v1.methods
        setState(data)
      }) as EventListener)

      document.addEventListener('kodix:onePlatform:smartContractBootFailed', (() => {
        logger.error('[SC/kodix:onePlatform:smartContractBootFailed] Falling back to non-SC mode')
        setFailed(true)
      }) as EventListener)

      document.addEventListener('kodix:smartContract:updated', ((e: CustomEvent<SmartContractListInitEvent>) => {
        logger.log('[SC/kodix:smartContract:updated]', e.detail)

        const data = e.detail.api.v1.data
        logger.log('[SC/DataUpdated]', data)
        setState(data)
      }) as EventListener)

      if (window.locator) {
        const widgetIdsInfo = await locator.locate<{
          dealershipToWidgetMap?: Record<string, WidgetIds>
          getWidgetIds: () => [WidgetIds]
        }>('smartContractWidgetsInfo')

        logger.log('[SC]: got widget ids', widgetIdsInfo)

        widgetIDByDealerChanged(widgetIdsInfo.dealershipToWidgetMap || {})

        const scWidgetIds = widgetIdsInfo
          .getWidgetIds()
          .map((widgetIds) => determineWidgetId(widgetIds, isNew ? 'new' : 'used'))
          .filter((id): id is string => Boolean(id))

        setWidgetId(scWidgetIds[0])

        logger.log('[SC]: locating SC service')
        await locator.locate('smartContract')
        logger.log('[SC]: service located')

        const carWithoutVin = cars.some((item) => item.vin === 'VIN:')
        const config: InitListSmartContractProps = {
          cars: carDataToSCCar(cars),
          widgetIds: scWidgetIds,
          preset: carWithoutVin ? 'url' : 'vin',
          listVersion: '2',
          renderButtons: !carViewSettings.isMoreAboutPrice
        }
        console.log('config', config)

        logger.log('[SC]: service located, calling InitSmartContract', config)
        window.InitSmartContract && window.InitSmartContract(config)
      } else {
        logger.error("Service-locator not detected! Check thin client's JS")
      }
    }
  }, [])

  useEffect(() => {
    if (state) {
      setCreditAvailable(!!state?.settings.creditAvailableOnList)
      setTradeInPrice(state.tradeIn.rate.result.possibleCarPrice || 0)

      setTradeInEnabled(state.settings.tradeInAvailableOnList && state.tradeIn.behaviourByUser === 'applied')
      if (!state.settings.creditAvailableOnList) setCreditFilterEnabled(false)
    }
  }, [state])

  return {
    state,
    api: methods,
    loading: !state?.settings.isWidgetLoaded,
    tradeInAvailable: !!state?.settings.tradeInAvailableOnList,
    creditAvailable: !!state?.settings.creditAvailableOnList,
    tradeInEnabled: !!useStore($tradeInEnabled),
    failed,
    init
  }
}

export const determineWidgetId = (widgetIds: WidgetIds, productType: ProductTypeProps) => {
  if (productType === 'new' && !!widgetIds.new) return widgetIds.new
  if (productType === 'used' && !!widgetIds.used) return widgetIds.used
  if (!widgetIds.any) logger.error(`Can't determine widget ID from `)
  return widgetIds.any || undefined
}

export type SCCarForInit = {
  additional_options_cost: number
  base_color_name: string
  brand_id: string
  brand_name: string
  complectation_id: string
  card_id: string
  credit_discount: number
  credit_tradein_discount: number
  dealer_code: string | undefined
  dealer_id: string | undefined
  dealers_code: string[]
  dealers_id: string[]
  default_image_url: string
  engine_power_horse_power: number
  generation_name: string
  insurance_discount: number
  is_new: 'new' | 'used' | 'unknown'
  link: string
  marketing_complectation_code: string
  marketing_complectation_id: string
  model_id: string
  model_full_name: string
  model_name: string
  price: number
  restyling_id: string
  tradein_discount: number
  vin: string
  year: string
}

export const carDataToSCCar = (cars: CarData[]): SCCarForInit[] => {
  return cars.map((carData) => ({
    link: carData.url,
    dealer_code: '',
    card_id: carData.id,
    credit_tradein_discount: carData.creditTradeInDiscount,
    credit_discount: carData.creditDiscount,
    complectation_id: carData.complectationId,
    base_color_name: carData.baseColor.name,
    additional_options_cost: carData.additionalEquipmentCost || 0,
    brand_name: carData.brand.name,
    brand_id: carData.brand.id,
    dealer_id: carData.dealers.length ? carData.dealers[0].id : undefined,
    dealers_code: carData.dealers.map((d) => d.code),
    dealers_id: carData.dealers.map((d) => d.id),
    default_image_url: carData.images[0]?.src || carData.defaultImageUrl,
    engine_power_horse_power: carData.enginePowerHorsePower,
    generation_name: carData.generation.name,
    insurance_discount: carData.insuranceDiscount,
    is_new: carData.productType,
    marketing_complectation_code: carData.marketingComplectation.code,
    marketing_complectation_id: carData.marketingComplectation.id,
    model_full_name: carData.title,
    model_id: carData.model.id,
    model_name: carData.model.name,
    price: carData.price || 0,
    price_with_discounts: carData.priceWithDiscounts,
    restyling_id: carData.restyling.id,
    vin: carData.vinRaw,
    tradein_discount: carData.tradeInDiscount,
    year: carData.yearRAW,
    mileage: carData.mileage
  }))
}
